import React, { useState } from "react";
import {
  Bot,
  Send,
  Sparkles,
  Loader,
  AlertCircle,
  RefreshCw,
  Copy,
  Check,
  Trash2,
} from "lucide-react";
import { useAIStore } from "../store/aiStore";

interface BlogAIAssistantProps {
  onContentSelect: (content: string) => void;
  onTitleSelect: (title: string) => void;
  onMetaSelect: (meta: string) => void;
}

export default function BlogAIAssistant({
  onContentSelect,
  onTitleSelect,
  onMetaSelect,
}: BlogAIAssistantProps) {
  const [prompt, setPrompt] = useState("");
  const [copied, setCopied] = useState(false);
  const {
    content,
    titleSuggestions,
    metaDescription,
    improvements,
    loading,
    error,
    generateContent,
    generateTitles,
    generateMeta,
    clearError,
    clearHistory,
    clearContent,
  } = useAIStore();

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    await generateContent(prompt);
    setPrompt("");
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRetry = () => {
    clearError();
    if (prompt) {
      generateContent(prompt);
    }
  };

  const handleClearConversation = () => {
    clearHistory();
    clearContent();
    setPrompt("");
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Bot className="h-6 w-6 text-[#F98B3D]" />
            <h2 className="text-lg font-semibold text-gray-900">
              AI Blog Assistant
            </h2>
          </div>
          <button
            onClick={handleClearConversation}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Clear conversation"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-4">
          <div className="relative">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the blog post you want to create..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F98B3D] focus:border-transparent resize-none"
              rows={4}
            />
            <button
              onClick={handleSubmit}
              disabled={loading || !prompt.trim()}
              className="absolute bottom-2 right-2 p-2 text-[#F98B3D] hover:text-[#e07a2c] disabled:opacity-50"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>

        {error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={handleRetry}
                  className="mt-2 inline-flex items-center text-sm text-red-700 hover:text-red-900"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
          </div>
        ) : content ? (
          <div className="space-y-6">
            <div className="prose max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Generated Content
              </h3>
              <div className="relative">
                <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                  {content}

                  {improvements && (
                    <>
                      <hr className="my-4 border-gray-200" />
                      <div className="text-gray-700">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Suggested Improvements:
                        </h4>
                        {improvements}
                      </div>
                    </>
                  )}
                </div>
                <div className="absolute top-2 right-2 space-x-2">
                  <button
                    onClick={() => handleCopy(content)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Copy content"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => onContentSelect(content)}
                    className="p-1 text-[#F98B3D] hover:text-[#e07a2c]"
                    title="Use content"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={generateTitles}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-md transition-colors duration-200"
              >
                Generate Title Suggestions
              </button>
              <button
                onClick={generateMeta}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-md transition-colors duration-200"
              >
                Generate Meta Description
              </button>
            </div>

            {titleSuggestions.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Title Suggestions
                </h4>
                <div className="space-y-2">
                  {titleSuggestions.map((title, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-2 rounded"
                    >
                      <span className="text-gray-700">{title}</span>
                      <button
                        onClick={() => onTitleSelect(title)}
                        className="text-[#F98B3D] hover:text-[#e07a2c]"
                      >
                        <Sparkles className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {metaDescription && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Meta Description
                </h4>
                <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <span className="text-gray-700">{metaDescription}</span>
                  <button
                    onClick={() => onMetaSelect(metaDescription)}
                    className="text-[#F98B3D] hover:text-[#e07a2c]"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
